import { ReactNode, useMemo } from 'react';
import { PurchaseOrderLine, NonConformanceReport } from '../../lib/api';
import { ChangelogEntry } from '../common/Changelog';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Package, MessageSquare } from 'lucide-react';
import POLineGRNTab from './POLineGRNTab';
import POLineASNTab from './POLineASNTab';
import POLinePricebookTab from './POLinePricebookTab';
import ShortageManagement from './ShortageManagement';
import POLineNCRTab from './POLineNCRTab';
import POLineConcessionTab from './POLineConcessionTab';
import CommunicationLog from './CommunicationLog';
import ChangeRequestsTab from './ChangeRequestsTab';
import { countChangeRequests } from './changeRequestGrouping';
import { pendingActionCount } from './pendingProposal';

interface POLineDetailTabsProps {
  line: PurchaseOrderLine;
  lineId: number;
  userType: 'customer' | 'supplier';
  activeTab: string;
  onTabChange: (tab: string) => void;
  /** The Detail tab's content, drawn by the modal that owns its formatters. */
  detailContent: ReactNode;
  /** The line's real change log — the Change Requests tab and its count. */
  changelogs: ChangelogEntry[];
  grnCount: number;
  /** Null = the NCRs could not be loaded; the label then carries no count. */
  ncrs: NonConformanceReport[] | null;
  /** CR-6409: null = could not be loaded. */
  asnCount: number | null;
  /** CR-6409: the other side's unread messages; null = could not be loaded. */
  unreadMessageCount: number | null;
  /** Reload the line after a change in the Change Requests tab. */
  onChangeRequestUpdate: () => Promise<void>;
  /** Reload the line after a shortage is resolved. */
  onShortageUpdate: () => void;
  /** CR-6409: the Communication tab has marked the other side's messages read. */
  onMessagesRead: () => void;
}

const TRIGGER_CLASS = 'data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary';

/** CR-6409: the "(n)" every count-bearing tab uses — GRN set the pattern. */
const withCount = (label: string, count: number | null) => (count === null ? label : `${label} (${count})`);

/**
 * The red pill this modal uses for something needing the viewer's attention —
 * the changes waiting on them, and (CR-6409) messages they have not read.
 * Nothing at zero: a pill that is always there stops being read.
 */
function AttentionPill({ count, label }: { count: number | null; label: string }) {
  if (!count) return null;
  return (
    <span
      className="ml-1 min-w-[1.25rem] rounded-full bg-red-500 px-1.5 text-xs font-semibold leading-5 text-white"
      aria-label={`${count} ${label}`}
      title={`${count} ${label}`}
    >
      {count}
    </span>
  );
}

/**
 * The PO Line pop-up's tab strip and panels.
 *
 * Lifted out of POLineDetailModal (CR-6409 touched it, so its 500-line limit
 * applies). CR-6409 also gave every tab that lists records a count: Change
 * Requests, ASN/Shipments, GRN and NCR read "(n)", and Communication carries a
 * red pill of the messages the viewer has not read.
 *
 * @param props - See {@link POLineDetailTabsProps}.
 * @returns The tabs, with the active one's panel.
 */
export default function POLineDetailTabs({
  line, lineId, userType, activeTab, onTabChange, detailContent, changelogs,
  grnCount, ncrs, asnCount, unreadMessageCount,
  onChangeRequestUpdate, onShortageUpdate, onMessagesRead,
}: POLineDetailTabsProps) {
  const { isOpenOrdersControlHidden, isConcessionEnabledForCustomer } = useMenuSettings();
  // CR-5884: Hide the Communication tab when admin enables "Hide Communication
  // Box" and the viewer is a supplier. Customer-side viewers keep full access.
  const hideCommunicationTab = isOpenOrdersControlHidden('hide_communication_box', userType === 'supplier');
  const changeRequestCount = useMemo(() => countChangeRequests(changelogs), [changelogs]);

  return (
    <Tabs
      // CR-6411: `initialTab` is caller-supplied, so a supplier-side
      // caller could land on the customer-only Pricebooks tab and get a
      // blank content area with no trigger to click back from. Fall
      // back to Detail, same guard shape as ContractPriceDetailModal.
      value={userType !== 'customer' && activeTab === 'pricebooks' ? 'detail' : activeTab}
      onValueChange={onTabChange}
      className="w-full"
    >
      <div className="border-b px-4 pt-2">
        <TabsList className="bg-transparent border-none">
          <TabsTrigger value="detail" className={TRIGGER_CLASS}>Detail</TabsTrigger>
          <TabsTrigger value="change-requests" className={TRIGGER_CLASS}>
            <span className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />{withCount('Change Requests', changeRequestCount)}
              {/* EXPEDITING-3: how many changes are waiting on this viewer. */}
              <AttentionPill count={pendingActionCount(line, userType)} label="waiting on you" />
            </span>
          </TabsTrigger>
          {/* CR-6411: Pricebooks sits immediately before ASN/Shipments.
              Customer-side only — contract pricing is customer-managed
              commercial data and stays hidden from suppliers (CR-5552);
              the endpoint enforces the same rule server-side. */}
          {userType === 'customer' && (
            <TabsTrigger value="pricebooks" className={TRIGGER_CLASS}>Pricebooks</TabsTrigger>
          )}
          <TabsTrigger value="asn" className={TRIGGER_CLASS}>{withCount('ASN/Shipments', asnCount)}</TabsTrigger>
          {userType === 'customer' && (
            <TabsTrigger value="grn" className={TRIGGER_CLASS}>{withCount('GRN', grnCount)}</TabsTrigger>
          )}
          {/* Customer review 11 Sep (item 30b): count like GRN (n). */}
          <TabsTrigger value="ncr" className={TRIGGER_CLASS}>{withCount('NCR', ncrs ? ncrs.length : null)}</TabsTrigger>
          {/* CR-6510: the drill-down the PO-line concession badge
              implies. Shown only when this line carries a raised
              concession, and only where its customer runs the
              workflow — the same per-line gate as raising one. */}
          {line.concession_status
            && isConcessionEnabledForCustomer(line.customer_id) && (
            <TabsTrigger value="concessions" className={TRIGGER_CLASS}>Concessions</TabsTrigger>
          )}
          {!hideCommunicationTab && (
            <TabsTrigger value="communication" className={TRIGGER_CLASS}>
              <span className="flex items-center gap-1">
                Communication
                {/* CR-6409: unread, not total — what a message count means to
                    anyone who has used a chat. Clears once the tab is opened. */}
                <AttentionPill count={unreadMessageCount} label="unread" />
              </span>
            </TabsTrigger>
          )}
          {userType === 'customer' && line.shortage_qty && (
            <TabsTrigger value="shortage" className={TRIGGER_CLASS}>
              <span className="flex items-center gap-2">
                Shortage
                {!line.shortage_resolved && <span className="bg-red-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">!</span>}
              </span>
            </TabsTrigger>
          )}
          {userType === 'supplier' && (
            <TabsTrigger value="cfc" className={TRIGGER_CLASS}>CFC</TabsTrigger>
          )}
        </TabsList>
      </div>

      <TabsContent value="detail" className="m-0 p-4">{detailContent}</TabsContent>

      <TabsContent value="grn" className="m-0 p-6"><POLineGRNTab lineId={lineId} orderedQty={parseFloat(line.quantity)} /></TabsContent>
      {userType === 'customer' && (
        <TabsContent value="pricebooks" className="m-0 p-6">
          <POLinePricebookTab lineId={lineId} partNumber={line.part_number} />
        </TabsContent>
      )}
      <TabsContent value="asn" className="m-0 p-6"><POLineASNTab lineId={lineId} /></TabsContent>
      {line.shortage_qty && (
        <TabsContent value="shortage" className="m-0 p-6"><ShortageManagement line={line} onUpdate={onShortageUpdate} /></TabsContent>
      )}
      <TabsContent value="cfc" className="m-0 p-6">
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">CFC (Confirmed Fixture Chart) information will be displayed here</p>
          <p className="text-sm text-muted-foreground mt-2">Delivery confirmation and scheduling</p>
        </div>
      </TabsContent>
      <TabsContent value="ncr" className="m-0 p-6"><POLineNCRTab lineId={lineId} /></TabsContent>
      <TabsContent value="concessions" className="m-0 p-6"><POLineConcessionTab lineId={lineId} /></TabsContent>
      <TabsContent value="change-requests" className="m-0 p-6">
        <ChangeRequestsTab line={line} changelogs={changelogs} userType={userType} onUpdate={onChangeRequestUpdate} />
      </TabsContent>
      {!hideCommunicationTab && (
        <TabsContent value="communication" className="m-0 p-6">
          <CommunicationLog lineId={lineId} poId={line.po} partNumber={line.part_number} onMessagesRead={onMessagesRead} />
        </TabsContent>
      )}
    </Tabs>
  );
}
